import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getRequiredCurrentUserClientId } from "../../../core/auth/tenant";
import {
  createSupportTicket,
  createTicketMessage,
  deleteSupportTicketById,
  deleteTicketMessageById,
  extractSupportTicket,
  extractSupportTicketList,
  extractTicketMessage,
  extractTicketMessages,
  getTicketMessageAttachmentUrl,
  getSupportTicketById,
  getSupportTicketsByClient,
  getTicketMessagesByTicket,
  mapTicketMessageToSupportResponse,
  mapTicketToSupportCase,
  toDateLabel,
  uploadTicketMessageAttachment,
  updateSupportTicketById,
  updateTicketMessageById,
} from "../services";
import { fetchSystemStatusServices as fetchSystemStatusServicesRequest } from "../services/systemStatusService";

const updateMessageInThread = (caseItem, messageId, updater) => {
  if (!caseItem) return caseItem;

  return {
    ...caseItem,
    replies: (caseItem.replies || []).map((item) =>
      item.id === messageId ? updater(item) : item,
    ),
    supportResponses: (caseItem.supportResponses || []).map((item) =>
      item.id === messageId ? updater(item) : item,
    ),
  };
};

const getStatusCounts = (services = []) => {
  const counts = {
    Operational: 0,
    "Partial Issues": 0,
    "Experiencing Delays": 0,
    Down: 0,
  };

  services.forEach((service) => {
    if (counts[service.status] !== undefined) {
      counts[service.status] += 1;
    }
  });

  return counts;
};

export const fetchSystemStatusThunk = createAsyncThunk(
  "support/fetchSystemStatus",
  async (_, { rejectWithValue }) => {
    try {
      return await fetchSystemStatusServicesRequest();
    } catch (error) {
      return rejectWithValue(error?.message || "Unable to load system status.");
    }
  },
);

export const fetchSupportCasesThunk = createAsyncThunk(
  "support/fetchCases",
  async (clientId, { getState, rejectWithValue }) => {
    const resolvedClientId = Number(clientId) || getRequiredCurrentUserClientId(getState());
    const response = await getSupportTicketsByClient(resolvedClientId, {
      limit: 100,
      offset: 0,
    });

    if (response?.error) {
      return rejectWithValue(response.error.message || "Unable to load support cases.");
    }

    const tickets = extractSupportTicketList(response);
    return tickets.map((ticket) => mapTicketToSupportCase(ticket));
  },
);

export const loadSupportCaseThreadThunk = createAsyncThunk(
  "support/loadCaseThread",
  async (caseItem, { rejectWithValue }) => {
    const [ticketResponse, messagesResponse] = await Promise.all([
      getSupportTicketById(caseItem.id),
      getTicketMessagesByTicket(caseItem.id, { limit: 100, offset: 0 }),
    ]);

    if (ticketResponse?.error) {
      return rejectWithValue(ticketResponse.error.message || "Unable to load case details.");
    }

    const ticket = extractSupportTicket(ticketResponse) || caseItem;
    const supportCase = mapTicketToSupportCase(ticket);
    const messages = extractTicketMessages(messagesResponse);

    supportCase.supportResponses = messages
      .map((message) => mapTicketMessageToSupportResponse(message))
      .filter((item) => item.message);

    return supportCase;
  },
);

export const createSupportCaseThunk = createAsyncThunk(
  "support/createCase",
  async ({ formData, clientId }, { getState, rejectWithValue }) => {
    const resolvedClientId = Number(clientId) || getRequiredCurrentUserClientId(getState());
    const caseTitle =
      String(formData?.subject || formData?.case_title || "").trim() ||
      "Support Request";
    const caseDescription = String(
      formData?.message || formData?.description || "",
    ).trim();

    if (!caseDescription) {
      return rejectWithValue("Please enter a message for your support request.");
    }

    const response = await createSupportTicket({
      client_id: resolvedClientId,
      case_title: caseTitle,
      case_description: caseDescription,
      case_status: "open",
    });

    if (response?.error) {
      return rejectWithValue(response.error.message || "Unable to create support case.");
    }

    const createdTicket = extractSupportTicket(response);
    return mapTicketToSupportCase(createdTicket || response);
  },
);

export const submitSupportContactFormThunk = createAsyncThunk(
  "support/submitContactForm",
  async ({ subject, message, clientId }, { getState, rejectWithValue }) => {
    const resolvedClientId = Number(clientId) || getRequiredCurrentUserClientId(getState());
    const response = await createSupportTicket({
      client_id: resolvedClientId,
      case_title: subject,
      case_description: message,
      case_status: "open",
    });

    if (response?.error) {
      return rejectWithValue(response.error.message || "Unable to submit support ticket.");
    }

    const createdTicket = extractSupportTicket(response);
    return mapTicketToSupportCase(createdTicket || response);
  },
);

export const sendSupportReplyThunk = createAsyncThunk(
  "support/sendReply",
  async ({ caseId, message, attachment, currentUserName }, { rejectWithValue }) => {
    const payload = {
      ticket_id: caseId,
      message_content: message,
      message_timestamp: new Date().toISOString(),
    };

    if (attachment) {
      payload.attachment_filename = attachment.name;
      payload.attachment_contenttype = attachment.type || "application/octet-stream";
    }

    const response = await createTicketMessage(payload);
    if (response?.error) {
      return rejectWithValue(response.error.message || "Unable to send reply.");
    }

    const createdMessage = extractTicketMessage(response);

    if (attachment) {
      const uploadUrl = String(
        createdMessage?.attachment_upload_url || createdMessage?.upload_url || "",
      ).trim();

      const uploadResponse = uploadUrl
        ? await uploadTicketMessageAttachment(uploadUrl, attachment)
        : {
            error: {
              message: "Attachment upload URL is missing from ticket message response.",
            },
          };

      if (uploadResponse?.error) {
        // Roll back the ticket message so we do not persist a message that claims to have an attachment.
        if (createdMessage?.id) {
          await deleteTicketMessageById(createdMessage.id);
        }

        return rejectWithValue(uploadResponse.error.message || "Unable to upload attachment.");
      }
    }

    return {
      caseId,
      reply: {
        id: createdMessage?.id,
        from: currentUserName,
        date: toDateLabel(Date.now()),
        message,
        hasAttachment: Boolean(attachment),
        attachmentName: attachment ? createdMessage?.attachment_filename || attachment?.name || "" : "",
        attachmentUrl: "",
      },
    };
  },
);

export const fetchSupportAttachmentUrlThunk = createAsyncThunk(
  "support/fetchAttachmentUrl",
  async ({ messageId }, { rejectWithValue }) => {
    const response = await getTicketMessageAttachmentUrl(messageId);
    if (response?.error) {
      return rejectWithValue(response.error.message || "Unable to load attachment.");
    }

    return {
      messageId,
      attachmentUrl: response.attachment_url,
    };
  },
);

export const editSupportMessageThunk = createAsyncThunk(
  "support/editMessage",
  async ({ messageId, messageContent }, { rejectWithValue }) => {
    const response = await updateTicketMessageById(messageId, {
      message_content: messageContent,
      message_timestamp: new Date().toISOString(),
    });

    if (response?.error) {
      return rejectWithValue(response.error.message || "Unable to edit message.");
    }

    const updatedMessage = extractTicketMessage(response);
    return {
      messageId,
      messageContent: updatedMessage?.message_content || messageContent,
      date: updatedMessage?.message_timestamp
        ? toDateLabel(updatedMessage.message_timestamp)
        : toDateLabel(Date.now()),
    };
  },
);

export const deleteSupportMessageThunk = createAsyncThunk(
  "support/deleteMessage",
  async (messageId, { rejectWithValue }) => {
    const response = await deleteTicketMessageById(messageId);
    if (response?.error) {
      return rejectWithValue(response.error.message || "Unable to delete message.");
    }

    return { messageId };
  },
);

export const updateSupportCaseStatusThunk = createAsyncThunk(
  "support/updateCaseStatus",
  async ({ caseId, status }, { rejectWithValue }) => {
    const response = await updateSupportTicketById(caseId, {
      case_status: status,
    });

    if (response?.error) {
      return rejectWithValue(response.error.message || "Unable to update case status.");
    }

    const ticket = extractSupportTicket(response);
    const updatedCase = mapTicketToSupportCase(ticket || { id: caseId, case_status: status });

    return {
      caseId,
      status: updatedCase.status,
    };
  },
);

export const deleteSupportCaseThunk = createAsyncThunk(
  "support/deleteCase",
  async (caseId, { rejectWithValue }) => {
    const response = await deleteSupportTicketById(caseId);
    if (response?.error) {
      return rejectWithValue(response.error.message || "Unable to delete case.");
    }
    return { caseId };
  },
);

const initialState = {
  cases: [],
  selectedCase: null,
  isLoadingCases: true,
  isSubmittingCase: false,
  isSubmittingContactForm: false,
  isLoadingCaseThread: false,
  casesError: "",
  actionError: "",
  contactFormError: "",
  contactFormSuccess: "",
  hasLoaded: false,
  systemStatusServices: [],
  systemStatusCounts: {
    Operational: 0,
    "Partial Issues": 0,
    "Experiencing Delays": 0,
    Down: 0,
  },
  systemStatusLoading: false,
  systemStatusError: "",
  systemStatusHasLoaded: false,
};

export const supportInitialState = initialState;

const supportSlice = createSlice({
  name: "support",
  initialState,
  reducers: {
    clearSupportActionError: (state) => {
      state.actionError = "";
    },
    clearSupportCasesError: (state) => {
      state.casesError = "";
    },
    clearSupportContactMessages: (state) => {
      state.contactFormError = "";
      state.contactFormSuccess = "";
    },
    setSelectedSupportCase: (state, action) => {
      state.selectedCase = action.payload;
    },
    clearSystemStatusError: (state) => {
      state.systemStatusError = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSupportCasesThunk.pending, (state) => {
        state.isLoadingCases = true;
        state.casesError = "";
      })
      .addCase(fetchSupportCasesThunk.fulfilled, (state, action) => {
        state.isLoadingCases = false;
        state.cases = action.payload;
        state.hasLoaded = true;
      })
      .addCase(fetchSupportCasesThunk.rejected, (state, action) => {
        state.isLoadingCases = false;
        state.casesError = action.payload || "Unable to load support cases.";
        state.cases = [];
        state.hasLoaded = true;
      })
      .addCase(loadSupportCaseThreadThunk.pending, (state) => {
        state.isLoadingCaseThread = true;
        state.actionError = "";
      })
      .addCase(loadSupportCaseThreadThunk.fulfilled, (state, action) => {
        state.isLoadingCaseThread = false;
        state.selectedCase = action.payload;
        state.cases = state.cases.map((item) =>
          item.id === action.payload.id ? action.payload : item,
        );
      })
      .addCase(loadSupportCaseThreadThunk.rejected, (state, action) => {
        state.isLoadingCaseThread = false;
        state.actionError = action.payload || "Unable to load case details.";
      })
      .addCase(createSupportCaseThunk.pending, (state) => {
        state.isSubmittingCase = true;
        state.actionError = "";
      })
      .addCase(createSupportCaseThunk.fulfilled, (state, action) => {
        state.isSubmittingCase = false;
        state.cases = [action.payload, ...state.cases];
      })
      .addCase(createSupportCaseThunk.rejected, (state, action) => {
        state.isSubmittingCase = false;
        state.actionError = action.payload || "Unable to create support case.";
      })
      .addCase(submitSupportContactFormThunk.pending, (state) => {
        state.isSubmittingContactForm = true;
        state.contactFormError = "";
        state.contactFormSuccess = "";
      })
      .addCase(submitSupportContactFormThunk.fulfilled, (state, action) => {
        state.isSubmittingContactForm = false;
        state.contactFormSuccess = "Your support ticket has been submitted.";
        state.cases = [action.payload, ...state.cases];
      })
      .addCase(submitSupportContactFormThunk.rejected, (state, action) => {
        state.isSubmittingContactForm = false;
        state.contactFormError = action.payload || "Unable to submit support ticket.";
      })
      .addCase(sendSupportReplyThunk.fulfilled, (state, action) => {
        if (!state.selectedCase || state.selectedCase.id !== action.payload.caseId) {
          return;
        }

        state.selectedCase = {
          ...state.selectedCase,
          replies: [...(state.selectedCase.replies || []), action.payload.reply],
        };
      })
      .addCase(sendSupportReplyThunk.rejected, (state, action) => {
        state.actionError = action.payload || "Unable to send reply.";
      })
      .addCase(fetchSupportAttachmentUrlThunk.pending, (state) => {
        state.actionError = "";
      })
      .addCase(fetchSupportAttachmentUrlThunk.rejected, (state, action) => {
        state.actionError = action.payload || "Unable to load attachment.";
      })
      .addCase(editSupportMessageThunk.fulfilled, (state, action) => {
        state.selectedCase = updateMessageInThread(
          state.selectedCase,
          action.payload.messageId,
          (item) => ({
            ...item,
            message: action.payload.messageContent,
            date: action.payload.date,
          }),
        );
      })
      .addCase(editSupportMessageThunk.rejected, (state, action) => {
        state.actionError = action.payload || "Unable to edit message.";
      })
      .addCase(deleteSupportMessageThunk.fulfilled, (state, action) => {
        if (!state.selectedCase) return;

        state.selectedCase = {
          ...state.selectedCase,
          replies: (state.selectedCase.replies || []).filter(
            (item) => item.id !== action.payload.messageId,
          ),
          supportResponses: (state.selectedCase.supportResponses || []).filter(
            (item) => item.id !== action.payload.messageId,
          ),
        };
      })
      .addCase(deleteSupportMessageThunk.rejected, (state, action) => {
        state.actionError = action.payload || "Unable to delete message.";
      })
      .addCase(updateSupportCaseStatusThunk.fulfilled, (state, action) => {
        state.cases = state.cases.map((item) =>
          item.id === action.payload.caseId
            ? { ...item, status: action.payload.status }
            : item,
        );

        if (state.selectedCase?.id === action.payload.caseId) {
          state.selectedCase = {
            ...state.selectedCase,
            status: action.payload.status,
          };
        }
      })
      .addCase(updateSupportCaseStatusThunk.rejected, (state, action) => {
        state.actionError = action.payload || "Unable to update case status.";
      })
      .addCase(deleteSupportCaseThunk.fulfilled, (state, action) => {
        state.cases = state.cases.filter((item) => item.id !== action.payload.caseId);
        if (state.selectedCase?.id === action.payload.caseId) {
          state.selectedCase = null;
        }
      })
      .addCase(deleteSupportCaseThunk.rejected, (state, action) => {
        state.actionError = action.payload || "Unable to delete case.";
      })
      .addCase(fetchSystemStatusThunk.pending, (state) => {
        state.systemStatusLoading = true;
        state.systemStatusError = "";
      })
      .addCase(fetchSystemStatusThunk.fulfilled, (state, action) => {
        state.systemStatusLoading = false;
        state.systemStatusServices = action.payload || [];
        state.systemStatusCounts = getStatusCounts(state.systemStatusServices);
        state.systemStatusHasLoaded = true;
      })
      .addCase(fetchSystemStatusThunk.rejected, (state, action) => {
        state.systemStatusLoading = false;
        state.systemStatusServices = [];
        state.systemStatusCounts = getStatusCounts([]);
        state.systemStatusError = action.payload || "Unable to load system status.";
        state.systemStatusHasLoaded = true;
      });
  },
});

export const {
  clearSupportActionError,
  clearSupportCasesError,
  clearSupportContactMessages,
  clearSystemStatusError,
  setSelectedSupportCase,
} = supportSlice.actions;

export const selectSupportState = (state) => state.support;
export const selectSystemStatusServices = (state) => state.support.systemStatusServices;
export const selectSystemStatusCounts = (state) => state.support.systemStatusCounts;
export const selectSystemStatusLoading = (state) => state.support.systemStatusLoading;
export const selectSystemStatusError = (state) => state.support.systemStatusError;
export const selectSystemStatusHasLoaded = (state) => state.support.systemStatusHasLoaded;

export default supportSlice.reducer;
